import {isSketchPointObject} from '../../mesh/sketch';
import type {
    SketchCircularPatternRelationBuildInput,
    SketchCircularPatternRelationBuildResult,
} from '../../../types/sketch/circular-pattern/SketchCircularPatternRelationBuilder';
import {SketchPatternedConstraint} from '../solver/constraint/relationDefinition/pattern/SketchPatternedConstraint';

/**
 * 通用圆周草图阵列聚合关系构建器。
 *
 * 本类只验证“种子目标组 ↔ 每个实例目标组”的一一映射并构造一条未绑定关系；
 * 不关心目标是点、直线、圆或其它草图实体，也不访问 Viewer、求解器和历史。
 */
export class SketchCircularPatternRelationBuilder {
    /**
     * 构建一个阵列定义拥有的唯一聚合关系。
     *
     * @param input 阵列定义、中心与各实例目标组。
     * @returns 待统一关系服务绑定的关系附件。
     */
    public build(input: SketchCircularPatternRelationBuildInput): SketchCircularPatternRelationBuildResult {
        if (input.seedTargets.length === 0) {
            throw new Error('圆周阵列聚合关系缺少种子目标。');
        }
        if (input.instanceGroups.length === 0) {
            throw new Error('圆周阵列聚合关系缺少派生实例目标。');
        }
        input.instanceGroups.forEach((instanceGroup) => {
            if (instanceGroup.targets.length !== input.seedTargets.length) {
                throw new Error(
                    `圆周阵列实例 ${instanceGroup.instanceIndex} 的目标数量与种子组不一致。`,
                );
            }
            instanceGroup.targets.forEach((target, targetIndex) => {
                const seedIsPoint = isSketchPointObject(input.seedTargets[targetIndex]!);
                if (isSketchPointObject(target) !== seedIsPoint) {
                    throw new Error(
                        `圆周阵列实例 ${instanceGroup.instanceIndex} 的目标 ${targetIndex} 类型与种子不一致。`,
                    );
                }
            });
        });

        const relation = new SketchPatternedConstraint({
            patternPersistentId: input.patternObject.persistentId,
            center2d: [input.center2d[0], input.center2d[1]],
            hasCenterPointReference: Boolean(input.centerPoint),
            seedTargetKinds: input.seedTargets.map((target) => (
                isSketchPointObject(target) ? 'point' : 'entity'
            )),
            rotateInstances: input.rotateInstances,
            instances: input.instanceGroups.map((instanceGroup) => ({
                instanceIndex: instanceGroup.instanceIndex,
                rotationRad: instanceGroup.rotationRad,
            })),
        });
        return {
            relationAttachment: {
                relation,
                targets: [
                    ...(input.centerPoint ? [input.centerPoint] : []),
                    ...input.seedTargets,
                    ...input.instanceGroups.flatMap((instanceGroup) => instanceGroup.targets),
                ],
            },
        };
    }
}
